/**
 * 聊天滚动位置：按“账号 + 宠物 + 频道”各记各的，只在内存里（刷新页面就没了，不写本机存储）。
 * 记的是“停在最底下”，或者“视线最上面那条消息的编号 + 它离消息区上沿多远”——只有编号和像素，不存消息内容。
 * 不属于当前账号的记录随时清掉：换账号打开通讯器时清；会话缓存被清掉（退出、登录、接受邀请时 queryClient.clear）时全清。
 * 切宠物（householdContext 的 removeQueries 会丢掉消息缓存，但留着会话）不清：切回来还回到那只宠物上次的位置。
 */
import type { QueryClient } from "@tanstack/react-query";

export interface ChatScrollMemo {
  atBottom: boolean;
  anchorId: string | null;
  /** 视线最上面那条消息的上沿，相对消息区上沿（px，可为负） */
  anchorOffset: number;
  /** 离开时最后一条消息的编号：回来时多出来的就是“有新消息” */
  lastId: string | null;
}

const memos = new Map<string, { userId: string; memo: ChatScrollMemo }>();

export function chatMemoKey(userId: string, petId: string, channel: string, householdId: string | null = null, homeId: string | null = null): string {
  return JSON.stringify(householdId === null && homeId === null ? [userId, petId, channel] : [userId, petId, channel, householdId, homeId]);
}

export function readChatMemo(key: string): ChatScrollMemo | null {
  return memos.get(key)?.memo ?? null;
}

export function writeChatMemo(key: string, memo: ChatScrollMemo): void {
  const [userId] = JSON.parse(key) as [string];
  memos.set(key, { userId, memo });
}

/** 只留这个账号的；userId 为空（没登录）时全清 */
export function keepOnlyUser(userId: string | null): void {
  for (const [key, entry] of memos) if (entry.userId !== userId) memos.delete(key);
}

const hooked = new WeakSet<QueryClient>();
/**
 * 会话查询（queryKeys.session = ["identity", "session"]）被移除 = 退出、登录、接受邀请时整个缓存被清（queryClient.clear）：滚动记录全清。
 * 只丢消息缓存（切宠物、离开太久被回收）不算：位置只是消息编号和像素，留着，回来按编号找得到就回原处，找不到就停在最新。
 * 每个 QueryClient 只挂一次，一直挂着（应用里只有一个）。
 */
export function forgetWithQueryCache(client: QueryClient): void {
  if (hooked.has(client)) return;
  hooked.add(client);
  client.getQueryCache().subscribe((event) => {
    if (event.type !== "removed") return;
    const [scope, kind] = event.query.queryKey;
    if (scope === "identity" && kind === "session") keepOnlyUser(null);
  });
}
